import React, { useState, useEffect } from 'react';
import { Progress, Button, Typography, App } from 'antd';
import { DownloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import MediumModal from '../Components/Modals/MediumModal';
import { generateCatalogueCSV } from '../../services/utils/csvGenerator';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../../data/base';

const { Text } = Typography;

/**
 * COMPONENTE DOWNLOAD CSV MODAL
 * =============================
 * 
 * Questo componente gestisce il download di un file CSV contenente tutti i dati del catalogo.
 * 
 * FLUSSO DI FUNZIONAMENTO:
 * 1. CONFERMA: Mostra dettagli di cosa verrà scaricato e chiede conferma
 * 2. CARICAMENTO DATI: Carica tutti i prodotti e varianti (progresso 1-20%)
 * 3. GENERAZIONE CSV: Genera il CSV (progresso 20-100%)
 * 4. COMPLETAMENTO: Mostra successo e permette download manuale
 * 
 * DATI CSV GENERATI:
 * - SKU CODE: Codice identificativo della variante
 * - EAN CODE: Codice EAN della variante
 * - UPC CODE: Codice UPC della variante
 * - MODEL NAME: Nome del modello (recuperato da Firebase)
 * - VTO LINK: Link per Virtual Try-On
 * - 3DViewer Single Link: Link per visualizzazione 3D singola
 * - 3DViewer Multiple Link: Link per visualizzazione 3D multipla
 */

const DownloadCSVModal = ({
    visible,
    onCancel,
    catalogueId,
    catalogueName,
    skuCount // Preso dalle statistiche del catalogo invece di calcolarlo qui
}) => {
    const { message } = App.useApp();
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [processedSkus, setProcessedSkus] = useState(0);
    const [currentPhase, setCurrentPhase] = useState(''); // 'loading' o 'generating'
    const [allProductsData, setAllProductsData] = useState([]);
    const [allVariantsData, setAllVariantsData] = useState({});

    useEffect(() => {
        if (visible) {
            // If we have a downloadUrl from previous generation, keep the complete state
            if (downloadUrl) {
                setIsGenerating(false);
                setProgress(100);
                setIsComplete(true);
                setCurrentPhase('');
            } else {
                // Reset states when modal opens fresh
                setIsGenerating(false);
                setProgress(0);
                setIsComplete(false);
                setProcessedSkus(0);
                setCurrentPhase('');
                setAllProductsData([]);
                setAllVariantsData({});
            }
        } else {
            // Only revoke URL when modal closes
            if (downloadUrl) {
                URL.revokeObjectURL(downloadUrl);
                setDownloadUrl(null);
            }
        }
    }, [visible, downloadUrl]);

    // Separate useEffect to handle cleanup when component unmounts
    useEffect(() => {
        return () => {
            if (downloadUrl) {
                URL.revokeObjectURL(downloadUrl);
            }
        };
    }, [downloadUrl]);

    // Funzione per caricare tutti i prodotti del catalogo
    const loadAllProducts = async (onProgress) => {
        try {
            onProgress(2, 'Connecting to database...');

            // Recupera tutti i prodotti dal database
            const productsRef = collection(db, "CataloguesProducts");
            const allProductsQuery = query(
                productsRef,
                where("catalogRef", "==", catalogueId),
                where("status", "==", "Pubblicato"),
                orderBy("lastUpdate", "desc")
            );

            onProgress(4, 'Loading products...');
            const querySnapshot = await getDocs(allProductsQuery);

            onProgress(6, 'Processing products data...');
            const brandRefs = new Set();
            const productsData = [];

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                if (window.applyAdultFilter && data.tags && data.tags.includes('KIDS')) {
                    return;
                }

                if (data.mainBrandRef) {
                    brandRefs.add(data.mainBrandRef);
                }

                productsData.push({
                    id: doc.id,
                    ...data,
                    modelName: data.modelName || data.nome_modello || 'Unknown Model',
                    imgUrl: data.imgUrl || data.image || null
                });
            });

            onProgress(8, 'Loading brand information...');
            // Recupera i dati dei brand
            const brandsMap = {};
            if (brandRefs.size > 0) {
                const brandArray = Array.from(brandRefs);
                const brandPromises = brandArray.map(async (brandRef, index) => {
                    try {
                        const brandDoc = await getDoc(doc(db, "MainBrands", brandRef));
                        if (brandDoc.exists()) {
                            const brandData = brandDoc.data();
                            brandsMap[brandRef] = brandData.nameBrand || brandData.nome_brand || 'Unknown Brand';
                        } else {
                            brandsMap[brandRef] = 'Unknown Brand';
                        }

                        // Aggiorna progresso per ogni brand caricato
                        const brandProgress = 8 + Math.round((index + 1) / brandArray.length * 4);
                        onProgress(brandProgress, `Loading brand ${index + 1}/${brandArray.length}...`);
                    } catch (error) {
                        console.error(`Error fetching brand ${brandRef}:`, error);
                        brandsMap[brandRef] = 'Unknown Brand';
                    }
                });

                await Promise.all(brandPromises);
            }

            onProgress(12, 'Finalizing products...');
            const validProducts = productsData.map(product => ({
                ...product,
                brandName: product.mainBrandRef ? brandsMap[product.mainBrandRef] : (product.brandName || product.brand || 'Unknown Brand'),
                isVisible: product.isVisible !== false
            }));

            onProgress(14, `Loaded ${validProducts.length} products successfully`);
            return validProducts;
        } catch (error) {
            console.error("Error loading all products:", error);
            throw error;
        }
    };

    // Funzione per caricare le varianti per tutti i prodotti
    const loadAllVariants = async (products, onProgress) => {
        try {
            onProgress(15, 'Preparing variants loading...');

            const variantsByProduct = {};

            // Inizializza gli array delle varianti per ogni prodotto
            products.forEach(product => {
                variantsByProduct[product.id] = [];
            });

            // Raccogliamo tutti gli ID delle varianti dai prodotti
            const allVariantIds = [];
            const productVariantMap = {};

            products.forEach(product => {
                if (product.list_variants && product.list_variants.length > 0) {
                    allVariantIds.push(...product.list_variants);
                    productVariantMap[product.id] = product.list_variants;
                }
            });

            if (allVariantIds.length === 0) {
                onProgress(20, 'No variants found');
                return variantsByProduct;
            }

            onProgress(16, `Loading ${allVariantIds.length} variants...`);

            // Carica le varianti in batch per migliorare le prestazioni
            const batchSize = 10;
            const totalBatches = Math.ceil(allVariantIds.length / batchSize);

            for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                const startIdx = batchIndex * batchSize;
                const batch = allVariantIds.slice(startIdx, startIdx + batchSize);

                // Progresso da 16% a 20% durante il caricamento delle varianti
                const variantProgress = 16 + Math.round((batchIndex + 1) / totalBatches * 4);
                onProgress(variantProgress, `Loading variants batch ${batchIndex + 1}/${totalBatches}...`);

                const variantPromises = batch.map(async (variantId) => {
                    try {
                        const variantRef = doc(db, "CataloguesVariants", variantId);
                        const variantSnap = await getDoc(variantRef);

                        if (variantSnap.exists()) {
                            const data = variantSnap.data();

                            // Trova il prodotto a cui appartiene questa variante
                            const productId = Object.keys(productVariantMap).find(pid =>
                                productVariantMap[pid].includes(variantId)
                            );

                            if (productId) {
                                // Normalizza lo stato della variante
                                let normalizedStatus = 'Published';
                                if (data.status) {
                                    const status = data.status.toLowerCase();
                                    if (status === 'draft' || status === 'bozza') {
                                        normalizedStatus = 'Draft';
                                    }
                                }

                                // Crea l'oggetto variante
                                const variant = {
                                    id: variantSnap.id,
                                    productId: productId,
                                    ...data,
                                    poster: data.poster || data.image || null,
                                    glassesName: data.glassesName || data.name || 'Unknown Variant',
                                    frameColor: data.frameColor || data.color || 'Unknown Color',
                                    lensesColor: data.lensesColor || data.lensColor || 'TRANSPARENT',
                                    skuModel: data.sku || data.skuModel || '',
                                    batch: data.batch || '',
                                    size: data.size || { size: '' },
                                    status: normalizedStatus
                                };

                                // Aggiungi la variante all'array del prodotto corrispondente
                                variantsByProduct[productId].push(variant);
                            }
                        }
                    } catch (error) {
                        console.error(`Error fetching variant ${variantId}:`, error);
                    }
                });

                await Promise.all(variantPromises);
            }

            const totalVariantsLoaded = Object.values(variantsByProduct).reduce((sum, variants) => sum + variants.length, 0);
            onProgress(20, `Loaded ${totalVariantsLoaded} variants successfully`);
            return variantsByProduct;
        } catch (error) {
            console.error("Error loading variants:", error);
            throw error;
        }
    };

    const handleConfirmDownload = async () => {
        try {
            setIsGenerating(true);
            setProgress(0);
            setProcessedSkus(0);
            setCurrentPhase('loading');

            // FASE 1: Caricamento prodotti e varianti (0-20%)
            const products = await loadAllProducts((progress, message) => {
                setProgress(progress);
                setCurrentPhase(`loading: ${message}`);
            });

            const variants = await loadAllVariants(products, (progress, message) => {
                setProgress(progress);
                setCurrentPhase(`loading: ${message}`);
            });

            setAllProductsData(products);
            setAllVariantsData(variants);

            // FASE 2: Generazione CSV (20-100%)
            setCurrentPhase('generating');

            const csvData = await generateCatalogueCSV(
                products,
                variants,
                catalogueId,
                (processed, total) => {
                    setProcessedSkus(processed);
                    // Il progresso va dal 20% al 100%
                    const csvProgress = Math.round(20 + ((processed / total) * 80));
                    setProgress(csvProgress);
                }
            );

            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            setDownloadUrl(url);

            const link = document.createElement('a');
            link.href = url;
            link.download = `${catalogueName}_catalogue_export.csv`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                setIsComplete(true);
                setCurrentPhase('');
                message.success('CSV generated and downloaded successfully!');
            }, 500);

        } catch (error) {
            console.error('Error during CSV generation:', error);
            message.error('Error generating CSV file');
            setIsGenerating(false);
            setCurrentPhase('');
        }
    };

    const handleManualDownload = () => {
        if (downloadUrl) {
            try {
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `${catalogueName}_catalogue_export.csv`;
                link.style.display = 'none';
                document.body.appendChild(link);

                link.click();
                document.body.removeChild(link);

                message.success('Download avviato!');
            } catch (error) {
                console.error('Error during manual download:', error);
                message.error('Errore durante il download manuale');
            }
        } else {
            console.error('No download URL available');
            message.error('URL di download non disponibile. Riprova a generare il file.');
        }
    };

    const handleClose = () => {
        if (downloadUrl) {
            URL.revokeObjectURL(downloadUrl);
        }
        onCancel();
    };

    const renderContent = () => {
        // ===============================
        // SEZIONE CONFERMA
        // ===============================
        if (!isGenerating && !isComplete) {
            return (
                <div style={{ padding: '0' }}>
                    <Text style={{
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: '#333',
                        display: 'block',
                        marginBottom: '20px'
                    }}>
                        Are you sure you want to download the catalog CSV list?
                    </Text>

                    <Text style={{
                        fontSize: '14px',
                        color: '#666',
                        display: 'block',
                        marginBottom: '16px'
                    }}>
                        The file will contain one sheet with:
                    </Text>

                    <ul style={{
                        marginTop: '0',
                        marginBottom: '20px',
                        paddingLeft: '20px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#666'
                    }}>
                        <li><strong>{skuCount}</strong> product variants with complete data</li>
                        <li>SKU, EAN, and UPC codes for product identification</li>
                        <li>Model names for easy reference</li>
                        <li>VTO (Virtual Try-On) links for web integration</li>
                        <li>3D visualization links (single and multiple views)</li>
                    </ul>

                    <Text style={{
                        fontSize: '13px',
                        color: '#8c8c8c',
                        fontStyle: 'italic',
                        display: 'block'
                    }}>
                        <strong>Note:</strong> The system will load all products and variants before generating the CSV. This may take a few moments for large catalogs.
                    </Text>
                </div>
            );
        }

        // ===============================
        // SEZIONE GENERAZIONE
        // ===============================
        if (isGenerating) {
            return (
                <div style={{ padding: '20px 0', textAlign: 'center' }}>
                    <Text style={{
                        fontSize: '16px',
                        color: '#333',
                        display: 'block',
                        marginBottom: '24px'
                    }}>
                        {currentPhase.startsWith('loading:')
                            ? 'Loading catalog data...'
                            : currentPhase === 'generating'
                                ? 'Generating your CSV file...'
                                : 'Processing...'
                        }
                    </Text>

                    <Progress
                        percent={progress}
                        status="active"
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }}
                        style={{ marginBottom: '16px' }}
                    />

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                    }}>
                        <Text style={{ fontSize: '14px', color: '#666' }}>
                            {currentPhase.startsWith('loading:')
                                ? currentPhase.replace('loading: ', '')
                                : currentPhase === 'generating'
                                    ? 'Processing variants...'
                                    : 'Please wait...'
                            }
                        </Text>
                        {currentPhase === 'generating' && (
                            <Text style={{ fontSize: '14px', color: '#1890ff', fontWeight: '500' }}>
                                {processedSkus} / {skuCount}
                            </Text>
                        )}
                    </div>

                    <Text style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {currentPhase.startsWith('loading:')
                            ? 'Please wait while we load all catalog data...'
                            : currentPhase === 'generating'
                                ? 'Please wait while we generate your CSV file.'
                                : 'Processing your request...'
                        }
                    </Text>
                </div>
            );
        }

        // ===============================
        // SEZIONE COMPLETAMENTO
        // ===============================
        if (isComplete) {
            return (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <CheckCircleOutlined
                        style={{
                            fontSize: '48px',
                            color: '#52c41a',
                            marginBottom: '20px'
                        }}
                    />
                    <Text style={{
                        fontSize: '18px',
                        color: '#52c41a',
                        fontWeight: '500',
                        display: 'block',
                        marginBottom: '16px'
                    }}>
                        CSV Generated Successfully!
                    </Text>
                    <Text style={{
                        fontSize: '14px',
                        color: '#666',
                        display: 'block',
                        marginBottom: '20px'
                    }}>
                        Your CSV file has been generated successfully.
                        <br />
                        The download should have started automatically. If not, use the button below.
                    </Text>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleManualDownload}
                        style={{
                            marginTop: '8px'
                        }}
                    >
                        Download CSV File
                    </Button>
                </div>
            );
        }
    };

    const renderFooter = () => {
        if (!isGenerating && !isComplete) {
            return [
                <Button
                    key="cancel"
                    onClick={handleClose}
                    style={{
                        marginRight: '12px'
                    }}
                >
                    Cancel
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    onClick={handleConfirmDownload}
                    disabled={skuCount === 0}
                >
                    Download
                </Button>
            ];
        }

        if (isGenerating) {
            return [
                <Button key="generating" disabled loading>
                    {currentPhase.startsWith('loading:') ? 'Loading Data...' : 'Generating CSV...'}
                </Button>
            ];
        }

        if (isComplete) {
            return [
                <Button key="close" onClick={handleClose}>
                    Close
                </Button>
            ];
        }
    };

    return (
        <MediumModal
            title="Download CSV"
            visible={visible}
            onClose={!isGenerating ? handleClose : () => { }}
            hideFooter={true}
        >
            <div>
                {renderContent()}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '24px',
                    paddingTop: '20px',
                    borderTop: '1px solid #f0f0f0'
                }}>
                    {renderFooter()}
                </div>
            </div>
        </MediumModal>
    );
};

export default DownloadCSVModal; 